import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Lock, CheckCircle2, Sparkles, ArrowRight, BookOpen, Eye, Handshake, Activity } from "lucide-react";
import { SELLER_STAGES } from "../../lib/stages";
import SellerQuestions from "../../components/SellerQuestions";
import { toast } from "sonner";
import { t } from "../../lib/i18n";

const OFFER_LABEL = { ioi: t("Oferta indicativa (IOI)"), loi: t("Carta de intenciones (LOI)"), dd: t("En Due Diligence"), closing: t("En cierre") };

export default function CompanyWizard() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [activeStage, setActiveStage] = useState(1);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/companies/${id}`);
      setCompany(r.data);
      setActiveStage(r.data.stage);
      setFormData(r.data.data?.[String(r.data.stage)] || {});
      try {
        const ir = await api.get("/interests/mine");
        setOffers((ir.data || []).filter((it) => it.company_id === id && ["ioi", "loi", "dd", "closing"].includes(it.status)));
      } catch { /* sin ofertas */ }
    })();
  }, [id]);

  if (!company) {
    return (
      <Layout>
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <span className="font-mono-label text-muted-foreground">{t("Cargando…")}</span>
        </div>
      </Layout>
    );
  }

  const isUnlocked = (stageId) => stageId <= company.stage;
  const isCompleted = (stageId) => company.completed_stages?.includes(stageId);
  const stage = SELLER_STAGES.find((s) => s.id === activeStage);

  const selectStage = (sid) => {
    if (!isUnlocked(sid)) return;
    setActiveStage(sid);
    setFormData(company.data?.[String(sid)] || {});
    setAiMessage("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/companies/${id}/stage`, {
        stage: activeStage,
        data: formData,
      });
      setCompany(r.data.company);
      setAiMessage(r.data.ai_message || "");
      toast.success(t("Etapa guardada"));
    } catch (e) {
      toast.error(t("Error guardando"));
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    if (activeStage < SELLER_STAGES.length && isUnlocked(activeStage + 1)) {
      selectStage(activeStage + 1);
    }
  };

  const completedCount = company.completed_stages?.length || 0;
  const pct = Math.round((completedCount / SELLER_STAGES.length) * 100);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="font-mono-label text-gold">{company.sector} · {company.country}</span>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-2">{company.name}</h1>
            <div className="mt-6 flex items-center gap-4 max-w-md">
              <div className="flex-1 h-1 bg-secondary relative">
                <div className="absolute inset-y-0 left-0 bg-[#1E3A5F]" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono-label">{completedCount}/{SELLER_STAGES.length} · {pct}%</span>
            </div>
            {company.score != null && (
              <div className="mt-4 hairline inline-flex items-center gap-3 px-4 py-2 bg-bone">
                <span className="font-mono-label text-gold">{t("Score IA")}</span>
                <span className="font-serif-display text-2xl">{company.score}<span className="text-xs text-muted-foreground">/100</span></span>
                {company.score_rationale && <span className="text-xs text-muted-foreground max-w-md">{company.score_rationale}</span>}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => window.location.assign(`/seller/company/${company.company_id}/questionnaire`)}
              variant="outline"
              className="rounded-none h-10 px-5"
              data-testid="wizard-open-questionnaire"
            >
              {t("Abrir cuestionario")}
            </Button>
            <Button
              onClick={() => window.location.assign(`/seller/company/${company.company_id}/teaser`)}
              disabled={!company.qualified}
              className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-10 px-5 disabled:opacity-50"
              data-testid="wizard-open-teaser"
            >
              {company.teaser_published ? t("Ver teaser publicado") : company.teaser_text ? t("Revisar teaser") : t("Generar teaser")}
            </Button>
            {company.teaser_published && (
              <span className="font-mono-label text-forest text-right">{t("Publicado para searchers")}</span>
            )}
            <Button
              onClick={() => window.location.assign(`/seller/company/${company.company_id}/im`)}
              disabled={!company.completed_stages?.includes(4)}
              variant="outline"
              className="rounded-none h-10 px-5 disabled:opacity-50"
              data-testid="wizard-open-im"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {company.im_sections ? t("Ver IM editable") : t("Generar IM")}
            </Button>
            {company.im_sections && (
              <Button
                onClick={() => window.location.assign(`/im/${company.company_id}?preview=1`)}
                variant="outline"
                className="rounded-none h-10 px-5 border-[#E0712C] text-[#C25F1E] hover:bg-[#E0712C]/5"
                data-testid="wizard-preview-im"
              >
                <Eye className="w-4 h-4 mr-2" />
                {t("Previsualizar IM publicado")}
              </Button>
            )}
            {company.im_published && (
              <Button
                onClick={() => window.location.assign(`/seller/company/${company.company_id}/accesos`)}
                variant="outline"
                className="rounded-none h-10 px-5"
                data-testid="wizard-access-log"
              >
                <Activity className="w-4 h-4 mr-2" />
                {t("Registro de accesos")}
              </Button>
            )}
            {company.im_published && (
              <span className="font-mono-label text-forest text-right">{t("IM publicado · visible para NDAs aprobados")}</span>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Stepper */}
          <aside className="lg:col-span-4">
            <div className="hairline bg-card sticky top-20">
              <div className="px-5 py-4 hairline-b bg-bone">
                <span className="font-mono-label text-muted-foreground">{t("Etapas")}</span>
              </div>
              {SELLER_STAGES.map((s, i) => {
                const unlocked = isUnlocked(s.id);
                const completed = isCompleted(s.id);
                const active = activeStage === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => selectStage(s.id)}
                    disabled={!unlocked}
                    data-testid={`wizard-stage-${s.id}`}
                    className={`w-full text-left flex items-start gap-4 px-5 py-4 ${i > 0 ? "hairline-t" : ""} ${
                      active ? "bg-[#1E3A5F] text-bone" : unlocked ? "hover:bg-bone" : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <div className={`w-8 h-8 hairline flex items-center justify-center flex-shrink-0 ${
                      active ? "border-bone" : ""
                    } ${completed ? "bg-forest border-forest text-bone" : ""}`}>
                      {completed ? <CheckCircle2 className="w-4 h-4" /> : !unlocked ? <Lock className="w-3 h-3" /> : <span className="font-serif-display">{s.id}</span>}
                    </div>
                    <div className="flex-1">
                      <div className={`font-mono-label ${active ? "text-gold" : "text-muted-foreground"}`}>{t("Etapa")}{" "}{s.id}</div>
                      <div className="font-serif-display text-lg leading-tight">{s.name}</div>
                      {!unlocked && s.locked_hint && (
                        <p className="text-xs text-muted-foreground mt-1 leading-snug" data-testid={`wizard-stage-${s.id}-hint`}>{s.locked_hint}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Form */}
          <section className="lg:col-span-8 space-y-6">
            {aiMessage && (
              <div className="hairline p-6 bg-bone flex items-start gap-4 fade-up" data-testid="wizard-ai-message">
                <div className="w-10 h-10 hairline flex items-center justify-center bg-[#1E3A5F] flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-gold" />
                </div>
                <div>
                  <span className="font-mono-label text-gold">{t("Tu asesor AI")}</span>
                  <p className="font-serif-display text-xl mt-1 leading-snug">{aiMessage}</p>
                </div>
              </div>
            )}

            <div className="hairline bg-card">
              <div className="px-7 py-6 hairline-b bg-bone">
                <span className="font-mono-label text-gold">{t("Etapa")}{" "}{stage.id}</span>
                <h2 className="font-serif-display text-3xl mt-1">{stage.name}</h2>
                <p className="text-sm text-muted-foreground mt-2">{stage.desc}</p>
              </div>
              <div className="p-7 space-y-6">
                {activeStage === 4 ? (
                  <div className="space-y-5">
                    <div className="hairline bg-bone p-6 flex items-start gap-4">
                      <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-serif-display text-xl mb-1">{t("El teaser se genera automáticamente")}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t("Ya no necesitas rellenar esta etapa. Nuestra IA redacta un teaser ciego profesional a partir de las etapas 1, 2 y 3. Tú lo revisas, editas si quieres, y decides cuándo publicarlo.")}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => window.location.assign(`/seller/company/${company.company_id}/teaser`)}
                      className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-11 px-6"
                      data-testid="wizard-go-teaser"
                    >
                      {company.teaser_text ? t("Ver y editar teaser") : t("Generar teaser ahora")} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : activeStage === 5 ? (
                  <div className="space-y-5">
                    <div className="hairline bg-bone p-6 flex items-start gap-4">
                      <BookOpen className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-serif-display text-xl mb-1">{t("El Information Memorandum se genera con IA")}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t("Nuestra IA redacta un IM dinámico con 4 secciones (Resumen Ejecutivo, Análisis del Mercado, Operaciones y Fortaleza Financiera) con enfoque comercial M&A a partir de tus etapas anteriores. Tú lo revisas, editas y decides cuándo publicarlo. Solo lo verán los searchers e inversores cuyo NDA firmado hayas aprobado.")}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => window.location.assign(`/seller/company/${company.company_id}/im`)}
                      className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-11 px-6"
                      data-testid="wizard-go-im"
                    >
                      {company.im_sections ? t("Ver y editar IM") : t("Generar IM ahora")} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : activeStage === 6 ? (
                  <SellerQuestions company={company} onCompanyUpdate={setCompany} />
                ) : (
                  <>
                    {activeStage === 7 && (
                      <div className="hairline bg-bone p-5 flex items-start gap-4" data-testid="wizard-loi-offers">
                        <Handshake className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <span className="font-mono-label text-gold">{t("Ofertas recibidas")}</span>
                          {offers.length === 0 ? (
                            <p className="text-muted-foreground mt-1">{t("Ningún searcher ha formalizado aún una oferta sobre esta empresa.")}</p>
                          ) : (
                            <ul className="mt-1 space-y-1">
                              {offers.map((o) => (
                                <li key={o.interest_id} data-testid={`wizard-offer-${o.interest_id}`}>
                                  <span className="font-medium">{o.searcher_name}</span>
                                  <span className="text-muted-foreground"> · {OFFER_LABEL[o.status] || o.status} · {new Date(o.updated_at || o.created_at).toLocaleDateString("es-ES")}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                    {stage.fields.map((f) => (
                      <div key={f.key} className="space-y-2">
                        <Label className="font-mono-label text-muted-foreground">{f.label}</Label>
                        {f.type === "textarea" ? (
                          <Textarea
                            value={formData[f.key] || ""}
                            onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                            rows={3}
                            className="rounded-none"
                            data-testid={`wizard-field-${f.key}`}
                          />
                        ) : f.type === "select" ? (
                          <Select
                            value={formData[f.key] || ""}
                            onValueChange={(v) => setFormData({ ...formData, [f.key]: v })}
                          >
                            <SelectTrigger className="rounded-none" data-testid={`wizard-field-${f.key}`}>
                              <SelectValue placeholder={t("Selecciona…")} />
                            </SelectTrigger>
                            <SelectContent>
                              {f.options.map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={f.type}
                            value={formData[f.key] || ""}
                            onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                            placeholder={f.placeholder}
                            className="rounded-none"
                            data-testid={`wizard-field-${f.key}`}
                          />
                        )}
                      </div>
                    ))}

                    <div className="flex items-center justify-between pt-4 hairline-t">
                      <span className="font-mono-label text-muted-foreground">
                        {isCompleted(activeStage) ? t("Etapa completada · puedes editar") : t("Completa para desbloquear la siguiente")}
                      </span>
                      <div className="flex gap-3">
                        <Button
                          onClick={save}
                          disabled={saving}
                          className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-11 px-6"
                          data-testid="wizard-save-btn"
                        >
                          {saving ? t("Guardando…") : t("Guardar etapa")}
                        </Button>
                        {activeStage < SELLER_STAGES.length && isUnlocked(activeStage + 1) && (
                          <Button
                            variant="outline"
                            onClick={goNext}
                            className="rounded-none h-11 px-6"
                            data-testid="wizard-next-btn"
                          >
                            {t("Siguiente")}{" "}<ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
